import React, { useState, useEffect } from 'react';
import { Box, TextField, Typography, InputAdornment, Card, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { GoogleMap, LoadScript, Marker, StandaloneSearchBox, Circle, OverlayView } from '@react-google-maps/api';
import { Search as SearchIcon } from '@mui/icons-material';
import axios from 'axios';
import ClusterOverviewModal from '../modals/ClusterOverviewModal';
import ClusterInsightsExpanded from '../modals/ClusterInsightsExpanded';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { dummyActiveLocations } from './dummyActiveLocations'; // Ensure this file has the LA coordinates
import SessionDetailModal from '../modals/SessionDetailModal'; // Import the new modal

const NeighborhoodsComponent = ({ mapCenter, setMapCenter, aiPins = [], isExpanded, onToggleExpand }) => {
    const [searchBox, setSearchBox] = useState(null);
    const [clusters, setClusters] = useState([]);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [cityNames, setCityNames] = useState({});
    const [showExpandedModal, setShowExpandedModal] = useState(false);
    const [mapView, setMapView] = useState('clusters');

    // State for the new Session Detail Modal
    const [selectedSession, setSelectedSession] = useState(null);

    useEffect(() => {
        const fetchClusters = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/clusters`);
                const validClusters = response.data.filter(cluster =>
                    cluster.centroid && Array.isArray(cluster.centroid.coordinates) && cluster.centroid.coordinates.length === 2
                );
                setClusters(validClusters);
            } catch (error) {
                console.error('Error fetching clusters:', error);
            }
        };

        fetchClusters();
    }, []);

    const fetchCityName = async (zipCode) => {
        if (cityNames[zipCode]) return cityNames[zipCode];
        try {
            const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
            const response = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json`, {
                params: { address: zipCode, key: apiKey }
            });

            if (response.data.results.length > 0) {
                const cityComponent = response.data.results[0].address_components.find(component =>
                    component.types.includes('locality') || component.types.includes('political')
                );
                if (cityComponent) {
                    setCityNames(prev => ({ ...prev, [zipCode]: cityComponent.long_name }));
                    return cityComponent.long_name;
                }
            }
        } catch (error) {
            console.error('Error fetching city name:', error);
        }
        return zipCode;
    };

    const handleClusterClick = async (cluster) => {
        const cityName = cluster.zipCodes.length > 0 ? await fetchCityName(cluster.zipCodes[0]) : 'Unknown';
        setSelectedCluster({ ...cluster, cityName });
    };

    const getCircleColor = (affluenceScore) => {
        const brightness = affluenceScore > 0 ? Math.min(255, 50 + affluenceScore * 2) : 50;
        return `rgb(${brightness}, ${brightness}, 255)`;
    };

    const handleViewChange = (event, newView) => {
        if (newView !== null) {
            setMapView(newView);
        }
    };

    return (
        <Card
            sx={{
                position: 'relative',
                backgroundColor: '#202020',
                color: '#fff',
                padding: '1rem',
                height: isExpanded ? '100%' : '720px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <IconButton
                onClick={onToggleExpand}
                sx={{ position: 'absolute', top: 16, right: 16, color: 'white', zIndex: 2 }}
            >
                {isExpanded ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
            </IconButton>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2, gap: 1.5 }}>
                <Typography variant="h6" sx={{ m: 0 }}>
                    Your Neighborhoods
                </Typography>
                <ToggleButtonGroup
                    value={mapView}
                    exclusive
                    onChange={handleViewChange}
                    aria-label="Map view toggle"
                    sx={{ backgroundColor: '#333', borderRadius: '8px' }}
                >
                    <ToggleButton value="clusters" aria-label="show clusters" sx={{
                        color: '#fff',
                        border: '1px solid #555',
                        textTransform: 'none',
                        '&.Mui-selected': {
                            backgroundColor: 'rgba(0, 122, 255, 0.5)',
                            color: '#fff',
                        },
                        '&.Mui-selected:hover': {
                            backgroundColor: 'rgba(0, 122, 255, 0.7)',
                        }
                    }}>
                        Clusters
                    </ToggleButton>
                    <ToggleButton value="activeLocations" aria-label="show active locations" sx={{
                        color: '#fff',
                        border: '1px solid #555',
                        textTransform: 'none',
                        '&.Mui-selected': {
                            backgroundColor: 'rgba(0, 122, 255, 0.5)',
                            color: '#fff',
                        },
                        '&.Mui-selected:hover': {
                            backgroundColor: 'rgba(0, 122, 255, 0.7)',
                        }
                    }}>
                        Active Locations
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY} libraries={['places']}>
                <StandaloneSearchBox onLoad={(ref) => setSearchBox(ref)} onPlacesChanged={() => {
                    const places = searchBox.getPlaces();
                    if (places.length > 0) {
                        setMapCenter({
                            lat: places[0].geometry.location.lat(),
                            lng: places[0].geometry.location.lng(),
                        });
                    }
                }}>
                    <TextField
                        variant="outlined"
                        placeholder="Search locations"
                        sx={{ width: '100%', mb: '1rem', backgroundColor: '#fff', zIndex: 1, position: 'relative' }}
                        InputProps={{
                            startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
                        }}
                    />
                </StandaloneSearchBox>

                <Box sx={{ flexGrow: 1, height: '100%' }}>
                    <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        center={{ lat: mapCenter.lat, lng: mapCenter.lng }}
                        zoom={mapCenter.zoom || 10}
                    >
                        {/* Always render cluster circles as the base layer */}
                        {clusters.map((cluster) => (
                            <Circle
                                key={cluster._id}
                                center={{ lat: cluster.centroid.coordinates[1], lng: cluster.centroid.coordinates[0] }}
                                radius={cluster.leadCount * 400}
                                options={{
                                    strokeColor: getCircleColor(cluster.affluenceScore),
                                    strokeOpacity: 0.8,
                                    strokeWeight: 2,
                                    fillColor: getCircleColor(cluster.affluenceScore),
                                    fillOpacity: 0.35,
                                }}
                                onClick={() => handleClusterClick(cluster)}
                            />
                        ))}

                        {/* Conditionally render Active Location markers on top of the clusters */}
                        {mapView === 'activeLocations' && dummyActiveLocations.map((location) => (
                            <Marker
                                key={`loc-${location.id}`}
                                position={{ lat: location.lat, lng: location.lng }}
                                icon={{ url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png' }}
                                onClick={() => setSelectedSession(location)}
                            />
                        ))}

                        {/* Show Cluster Modal whenever a cluster is selected */}
                        {selectedCluster && (
                            <OverlayView
                                position={{ lat: selectedCluster.centroid.coordinates[1], lng: selectedCluster.centroid.coordinates[0] }}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            >
                                <ClusterOverviewModal
                                    cluster={selectedCluster}
                                    onClose={() => setSelectedCluster(null)}
                                    onExpand={() => setShowExpandedModal(true)}
                                />
                            </OverlayView>
                        )}

                        {aiPins.map((pin, index) => (
                            <Marker
                                key={`ai-${index}`}
                                position={{ lat: pin.lat, lng: pin.lng }}
                                icon={{ url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png' }}
                            />
                        ))}

                        <Marker position={mapCenter} />
                    </GoogleMap>

                    {showExpandedModal && selectedCluster && (
                        <ClusterInsightsExpanded
                            cluster={selectedCluster}
                            onClose={() => setShowExpandedModal(false)}
                        />
                    )}
                </Box>
            </LoadScript>

            {/* Render the new SessionDetailModal when a session is selected */}
            <SessionDetailModal
                session={selectedSession}
                onClose={() => setSelectedSession(null)}
            />
        </Card>
    );
};

export default NeighborhoodsComponent;
